import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownSelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setIsDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener');
  };

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(https://readdy.ai/api/search-image?query=Artistic%20editorial%20flat%20lay%20photography%20featuring%20an%20eclectic%20curated%20arrangement%20of%20contemporary%20fashion%20accessories%20including%20a%20woven%20straw%20handbag%20with%20leather%20trim%20a%20pair%20of%20tortoiseshell%20sunglasses%20a%20silk%20twill%20scarf%20with%20abstract%20botanical%20print%20and%20a%20minimalist%20ceramic%20jewelry%20dish%20arranged%20on%20a%20textured%20warm%20oatmeal%20linen%20fabric%20surface%20with%20gentle%20directional%20window%20light%20casting%20soft%20elongated%20shadows%20sophisticated%20warm%20neutral%20palette%20with%20touches%20of%20earthy%20terracotta%20and%20olive%20green%20editorial%20composition&width=1600&height=900&seq=hero-bg-v2-june-oopbuy&orientation=landscape`,
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>

      <div className="relative z-10 w-full px-4 md:px-6 lg:px-12 py-32 md:py-40">
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
          <div className="flex-1 w-full">
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight tracking-tight animate-fade-in-up">
              OOPBuy Spreadsheet
              <br />
              <span className="text-accent-300">The Most Advanced</span>
              <br />
              <span className="text-background-50/80">Product Sourcing System</span>
            </h1>

            <p className="mt-6 max-w-xl text-base md:text-lg text-background-100/80 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              A structured ecommerce spreadsheet framework for product comparison, sourcing optimization, and data-driven shopping decisions — designed to replace chaotic browsing with systematic analysis.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Access OOPBuy Spreadsheet
              </a>
              <Link
                to="/what-is-oopbuy-spreadsheet"
                className="px-6 py-3 text-sm font-semibold border border-background-100/40 text-background-50 rounded-md hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer"
              >
                Explore Sourcing Guides
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-2.5 text-sm font-bold bg-accent-500 text-background-50 rounded-md hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>

          <div className="w-full lg:w-[380px] flex-shrink-0 animate-fade-in-up" style={{ animationDelay: '0.25s' }}>
            <div className="bg-background-50/95 backdrop-blur-sm rounded-lg p-6 border border-background-200/60">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-4">
                Browse Product Categories
              </p>

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-background-100 rounded-md border border-background-200 text-sm text-foreground-700 hover:border-background-300 transition-colors cursor-pointer"
                >
                  <span className={selectedCategory ? 'text-foreground-950' : 'text-foreground-400'}>
                    {selectedCategory || 'Select a Category'}
                  </span>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </span>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full left-0 w-full mt-1 bg-background-50 rounded-md border border-background-200/80 shadow-sm py-1 z-50 max-h-64 overflow-y-auto animate-fade-in">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleDropdownSelect(cat)}
                        className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <p className="mt-4 text-xs text-foreground-500 leading-relaxed">
                Select a category above to navigate directly to structured product listings on the OOPBuy Spreadsheet platform. Each category contains curated, organized product data for efficient comparison.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}